import React, { useEffect, useRef, useState } from "react";
import "./CreateAndEditResource.css";
import {
  CloudUpload,
  CrossIcon,
  DeleteIcon,
} from "../../../../icons/reactIcons";
import CustomEditor from "../../../../components/custom-editor/CustomEditor";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchResourceCategoriesThunk,
  fetchResourceByIdThunk,
  clearSelectedResource,
  updateResourceThunk,
  createResourceThunk,
} from "../../../../redux/slices/resourceSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import { fetchCmsFileByIdThunk } from "../../../../redux/slices/fileSlice";
import {
  UpdateResourcePayload,
  UpdateDocumentPayload,
} from "../../../../apis/resourceApis";
import { uploadCmsFile } from "../../../../apis/fileApis";
import Loader from "../../../../components/custom-loader/Loader";
import { toast } from "react-toastify";
import { truncateFileName } from "../../../../utils/truncateFileName";
import {
  isAllowedImageFile,
  isAllowedLessonOrResourceFile,
} from "../../../../utils/fileValidation";
import {
  isValidVideoUrl,
  isValidWebsiteUrl,
} from "../../../../utils/urlValidation";

interface Attachment {
  id: string | number;
  type: string;
  file?: File | null;
  url?: string;
  duration?: string;
  isExisting?: boolean;
  documentFileId?: string;
}

const MAX_FIELD_LENGTH = 100;
const MAX_DETAILS_LENGTH = 1000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const getPlainText = (value: string) =>
  value
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const isFileTooLarge = (file: File) => file.size > MAX_FILE_SIZE;

const CreateAndEditResource: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { categories, selectedResource, loading } = useAppSelector(
    (state) => state.resource,
  );
  const { fileDetail } = useAppSelector((state) => state.file);

  const thumbnailRef = useRef<HTMLInputElement | null>(null);
  const documentRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    altText: "",
  });

  const [details, setDetails] = useState("");
  const [thubNail, setThumbNail] = useState<File | string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([
    { id: Date.now(), type: "Document", file: null, url: "", duration: "" },
  ]);

  const [attachmentFileNames, setAttachmentFileNames] = useState<{
    [key: string]: string;
  }>({});

  /* ================= EFFECTS ================= */

  useEffect(() => {
    dispatch(fetchResourceCategoriesThunk());
    return () => {
      dispatch(clearSelectedResource());
    };
  }, [dispatch]);

  useEffect(() => {
    if (resourceId) {
      dispatch(fetchResourceByIdThunk(resourceId));
    }
  }, [resourceId, dispatch]);

  useEffect(() => {
    if (selectedResource && resourceId) {
      setFormData({
        title: selectedResource.name,
        category: selectedResource.categoryId || "",
        altText: selectedResource.altText,
      });

      setDetails(selectedResource.description);
      setThumbNail(selectedResource.thumbnail);

      if (selectedResource.thumbnail) {
        dispatch(fetchCmsFileByIdThunk(selectedResource.thumbnail));
      }

      const mappedAttachments = selectedResource.document.map(
        (doc: any, index: number) => {
          if (doc.documentType === "file" && doc.documentFileId) {
            dispatch(fetchCmsFileByIdThunk(doc.documentFileId)).then(
              (action: any) => {
                if (fetchCmsFileByIdThunk.fulfilled.match(action)) {
                  setAttachmentFileNames((prev) => ({
                    ...prev,
                    [doc.documentFileId]: action.payload.maskName,
                  }));
                }
              },
            );
          }

          return {
            id: `existing-${index}`,
            type:
              doc.documentType === "file"
                ? "Document"
                : doc.documentType === "video"
                  ? "Video"
                  : "Website",
            url: doc.documentLink,
            duration: doc.documentDuration,
            documentFileId: doc.documentFileId || "",
            isExisting: true,
          };
        },
      );

      setAttachments(
        mappedAttachments.length
          ? mappedAttachments
          : [{ id: Date.now(), type: "Document", file: null }],
      );
    }
  }, [selectedResource, resourceId, dispatch]);

  /* ================= HANDLERS ================= */

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    if (file && isFileTooLarge(file)) {
      toast.error("Files can be a maximum of 10 MB");
      return;
    }

    if (file && !isAllowedImageFile(file)) {
      toast.error("Only image files are allowed");
      return;
    }

    setThumbNail(file);
  };

  const handleAddAttachment = () => {
    setAttachments([
      ...attachments,
      { id: Date.now(), type: "Document", file: null },
    ]);
  };

  const handleRemoveAttachment = (id: string | number) => {
    setAttachments(attachments.filter((a) => a.id !== id));
  };

  const handleAttachmentChange = (
    id: string | number,
    field: keyof Attachment,
    value: any,
  ) => {
    if (field === "file" && value instanceof File && isFileTooLarge(value)) {
      toast.error("Files can be a maximum of 10 MB");
      return;
    }

    if (
      field === "file" &&
      value instanceof File &&
      !isAllowedLessonOrResourceFile(value)
    ) {
      toast.error("Only image and PDF files are allowed");
      return;
    }

    if (
      (field === "url" || field === "duration") &&
      typeof value === "string" &&
      value.length > MAX_FIELD_LENGTH
    ) {
      return;
    }

    setAttachments(
      attachments.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
    );
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error("Category is required");
      return;
    }

    if (!formData.title.trim()) {
      toast.error("Resource name is required");
      return;
    }

    if (!formData.altText.trim()) {
      toast.error("Alt text is required");
      return;
    }

    if (formData.title.trim().length > MAX_FIELD_LENGTH) {
      toast.error("Resource name must be 100 characters or less");
      return;
    }

    if (formData.altText.trim().length > MAX_FIELD_LENGTH) {
      toast.error("Alt text must be 100 characters or less");
      return;
    }

    /* ---------- THUMBNAIL VALIDATION ---------- */

    if (!thubNail) {
      toast.error("Thumbnail is required");
      return;
    }

    /* ---------- RESOURCE DETAILS VALIDATION ---------- */

    // remove HTML tags to check actual content
    const plainText = getPlainText(details);

    if (!plainText) {
      toast.error("Resource details are required");
      return;
    }

    if (plainText.length > MAX_DETAILS_LENGTH) {
      toast.error("Resource details must be 1000 characters or less");
      return;
    }

    for (let i = 0; i < attachments.length; i++) {
      const att = attachments[i];

      if (att.type === "Document") {
        if (!att.file && !att.documentFileId) {
          toast.error(`Attachment ${i + 1}: File is required`);
          return;
        }
      }

      if (att.type === "Video") {
        if (!att.url) {
          toast.error(`Attachment ${i + 1}: Video URL is required`);
          return;
        }
        if (!att.duration) {
          toast.error(`Attachment ${i + 1}: Duration is required`);
          return;
        }
        if (!isValidVideoUrl(att.url)) {
          toast.error(`Attachment ${i + 1}: Please enter a valid video URL`);
          return;
        }
      }

      if (att.type === "Website") {
        if (!att.url) {
          toast.error(`Attachment ${i + 1}: Website URL is required`);
          return;
        }
        if (!isValidWebsiteUrl(att.url)) {
          toast.error(`Attachment ${i + 1}: Please enter a valid website URL`);
          return;
        }
      }
    }

    try {
      /** ---------- THUMBNAIL ---------- */
      let finalThumbnailId = typeof thubNail === "string" ? thubNail : "";

      if (thubNail instanceof File) {
        finalThumbnailId = await uploadCmsFile(thubNail, "thumbnail");
      }

      if (!finalThumbnailId) {
        toast.error("Thumbnail is required");
        return;
      }

      /** ---------- DOCUMENTS ---------- */
      const documentPayload: UpdateDocumentPayload[] = await Promise.all(
        attachments.map(async (att) => {
          if (att.type === "Document") {
            let fileId = att.documentFileId || "";
            if (att.file instanceof File) {
              fileId = await uploadCmsFile(att.file, "attachment");
            }
            return {
              documentType: "file",
              documentFileId: fileId,
              isActive: true,
            };
          }

          if (att.type === "Video") {
            return {
              documentType: "video",
              documentLink: att.url || "",
              documentDuration: att.duration || "",
              isActive: true,
            };
          }

          return {
            documentType: "site",
            documentLink: att.url || "",
            isActive: true,
          };
        }),
      );

      /** ---------- PAYLOAD ---------- */
      const payload: UpdateResourcePayload = {
        name: formData.title,
        description: details,
        altText: formData.altText,
        thumbnail: finalThumbnailId,
        categoryId: formData.category,
        document: documentPayload,
      };

      /** ---------- CREATE / UPDATE ---------- */
      const action = resourceId
        ? await dispatch(
            updateResourceThunk({ resourceID: resourceId, payload }),
          )
        : await dispatch(createResourceThunk({ payload }));

      if (
        updateResourceThunk.fulfilled.match(action) ||
        createResourceThunk.fulfilled.match(action)
      ) {
        navigate("/resources");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Failed to save resource");
    }
  };

  if (loading && resourceId)
    return (
      <div>
        <Loader />
      </div>
    );

  return (
    <>
      <h2 className="page-title">
        {resourceId ? "Edit Resource" : "Create Resource"}
      </h2>
      <div className="resource-form">
        <form onSubmit={handleSubmit}>
          <div className="resource-form-group">
            <div className="resource-form-field left">
              <label>
                Category<span className="required">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                required
              >
                <option value="">Select</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="resource-form-field right">
              <label>
                Resource Name<span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    title: e.target.value.slice(0, MAX_FIELD_LENGTH),
                  })
                }
                maxLength={MAX_FIELD_LENGTH}
                required
              />
            </div>
          </div>

          <div className="resource-form-group">
            <div className="resource-form-field left">
              <label>
                Thumbnail<span className="required">*</span>
              </label>
              <div className="upload-btn">
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    handleThumbnailUpload(e);
                    e.target.value = "";
                  }}
                  ref={thumbnailRef}
                />
                <button
                  type="button"
                  onClick={() => thumbnailRef.current?.click()}
                >
                  Upload File <CloudUpload />
                </button>
              </div>
              {thubNail && (
                <div className="uploaded-thumbnail-btn">
                  {typeof thubNail === "string"
                    ? fileDetail?.id === thubNail
                      ? truncateFileName(fileDetail?.maskName)
                      : "Loading..."
                    : truncateFileName(thubNail.name)}
                  <div onClick={() => setThumbNail(null)}>
                    <CrossIcon className="remove-icon" />
                  </div>
                </div>
              )}
            </div>
            <div className="resource-form-field right">
              <label>
                Alt text (for screen readers)<span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.altText}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    altText: e.target.value.slice(0, MAX_FIELD_LENGTH),
                  })
                }
                maxLength={MAX_FIELD_LENGTH}
                required
              />
            </div>
          </div>

          <div className="attachments-section">
            <h3>Attachments</h3>
            {attachments.map((att, i) => (
              <div key={att.id} className="resource-form-group attachment-row">
                <div className="resource-attachment-field">
                  <label>
                    Attachment Type<span className="required">*</span>
                  </label>
                  <select
                    value={att.type}
                    onChange={(e) =>
                      handleAttachmentChange(att.id, "type", e.target.value)
                    }
                  >
                    <option value="Document">Document</option>
                    <option value="Video">Video</option>
                    <option value="Website">Website</option>
                  </select>
                </div>
                <div className="resource-attachment-right">
                  {att.type === "Document" && (
                    <div className="resourse-document-area">
                      <div className="resource-document-field">
                        <label>
                          Upload File<span className="required">*</span>
                        </label>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];

                            if (!file) return;

                            handleAttachmentChange(att.id, "file", file);
                            e.target.value = "";
                          }}
                          hidden
                          ref={(el) => {
                            documentRefs.current[att.id] = el;
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => documentRefs.current[att.id]?.click()}
                        >
                          Upload File <CloudUpload />
                        </button>
                      </div>
                      {(att.file || (att.isExisting && att.documentFileId)) && (
                        <div className="uploaded-attachment">
                          {att.file
                            ? truncateFileName(att.file.name)
                            : truncateFileName(
                                attachmentFileNames[att.documentFileId!],
                              ) || "Loading Existing Document..."}
                          <div
                            className="remove-icon"
                            onClick={() =>
                              handleAttachmentChange(att.id, "file", null)
                            }
                          >
                            <CrossIcon />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Video and Website sections remain same */}
                  {att.type === "Video" && (
                    <div className="resource-video">
                      <div className="resource-video-duration-field">
                        <label>
                          Duration<span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="00:00"
                          value={att.duration}
                          maxLength={MAX_FIELD_LENGTH}
                          onChange={(e) =>
                            handleAttachmentChange(
                              att.id,
                              "duration",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      <div className="resource-video-url-field">
                        <label>
                          Video URL<span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter Video URL"
                          value={att.url}
                          maxLength={MAX_FIELD_LENGTH}
                          onChange={(e) =>
                            handleAttachmentChange(
                              att.id,
                              "url",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  )}
                  {att.type === "Website" && (
                    <div className="resource-website-field">
                      <label>
                        Website URL<span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter Website URL"
                        value={att.url}
                        maxLength={MAX_FIELD_LENGTH}
                        onChange={(e) =>
                          handleAttachmentChange(att.id, "url", e.target.value)
                        }
                      />
                    </div>
                  )}
                </div>
                {i !== 0 && (
                  <div className="delete-attachment">
                    <DeleteIcon
                      style={{ marginTop: "2.4rem", cursor: "pointer" }}
                      onClick={() => handleRemoveAttachment(att.id)}
                    />
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddAttachment}
              className="add-attachment-btn"
            >
              + Add more Attachments
            </button>
          </div>
          <div>
            <label htmlFor="">
              Resource Details <span className="required">*</span>
            </label>
            <CustomEditor
              value={details}
              onChange={(value) => {
                if (getPlainText(value).length > MAX_DETAILS_LENGTH) {
                  return;
                }

                setDetails(value);
              }}
              placeholder="Enter Resource Details"
              maxLength={MAX_DETAILS_LENGTH}
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate("/resources")}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? "Processing..." : resourceId ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateAndEditResource;
